// MP-OS-204: the one text layer for jsPDF. The PDFs use jsPDF's built-in helvetica, a
// 1-byte WinAnsi (cp1252) font with no embedded glyphs. jsPDF maps the 27 cp1252 characters
// above U+00FF (– — ’ “ ” • … € ™ …) to their WinAnsi byte itself, but a single character
// outside WinAnsi (U+202F from number grouping, a thin space, U+2212, ≥, an emoji in a brand
// name) switches the WHOLE string to 2-byte UCS-2 against that 1-byte font: every character
// gets a NUL byte in front and the foreign one becomes two unrelated glyphs (U+202F reads
// " /", U+2212 reads '"'). So every string bound for pdf.text or splitTextToSize goes through
// toWinAnsi first: WinAnsi characters keep their real glyph, the rest get a defined
// replacement, never a silent drop or a garbled line.
import type { jsPDF, TextOptionsLight } from 'jspdf'

// cp1252 0x80–0x9F: the WinAnsi characters above U+00FF that jsPDF encodes itself.
const CP1252_HIGH = new Set('€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ')

const NBSP = '\u00a0'

// Characters outside WinAnsi with an obvious WinAnsi spelling. Invisible format characters
// (zero-width space/joiners, word joiner, BOM, variation selectors) have no glyph to keep and
// map to ''.
const REPLACE: Record<string, string> = {
  '\t': ' ',
  // Spaces: ogham, en/em quad, en/em, three/four/six-per-em, punctuation, thin, hair, medium
  // mathematical, ideographic. Figure space and narrow no-break space are no-break by
  // definition (format.ts groups digits with U+202F), so they become the WinAnsi NBSP and a
  // number such as "12 345" never wraps between its digit groups; see toWinAnsi for the others.
  '\u1680': ' ', '\u2000': ' ', '\u2001': ' ', '\u2002': ' ', '\u2003': ' ', '\u2004': ' ',
  '\u2005': ' ', '\u2006': ' ', '\u2007': NBSP, '\u2008': ' ', '\u2009': ' ', '\u200a': ' ',
  '\u202f': NBSP, '\u205f': ' ', '\u3000': ' ',
  '​': '', '‌': '', '‍': '', '⁠': '', '﻿': '', '︎': '', '️': '',
  // Dashes and minus signs
  '‐': '-', '‑': '-', '‒': '–', '―': '—', '⁃': '-', '−': '-',
  '﹘': '-', '﹣': '-', '－': '-',
  // Quotes and primes
  '‛': '‘', '‟': '“', '′': "'", '″': '"',
  // Dots
  '‧': '·', '∙': '·', '⋅': '·',
  // Comparison and arrows
  '≤': '<=', '≥': '>=', '≠': '!=', '≈': '~',
  '←': '<-', '→': '->', '↔': '<->', '⇒': '=>',
  '№': 'No.', '℠': 'SM',
}

// Latin Extended-A/B letters that NFD does not reduce to WinAnsi (a stroke, bar, hook or a
// letter of its own), spelled with the base letter a reader would expect: "Łódź" prints
// "Lódz", not "?ódz". Checked against every letter in U+0100–U+024F and U+1E00–U+1EFF;
// Œ œ Š š Ž ž Ÿ ƒ ß Ø ø Ð ð Þ þ are WinAnsi themselves and stay as written.
const LETTERS: Record<string, string> = Object.fromEntries(([
  // Latin Extended-A: Polish, Croatian/Serbian, Maltese, Turkish, Sami, Greenlandic, Afrikaans
  'ŁL', 'łl', 'ĐD', 'đd', 'ĦH', 'ħh', 'ıi', 'ĸk', 'ŊN', 'ŋn', 'ŦT', 'ŧt', ['ŉ', "'n"],
  // Latin Extended-B: stroke, bar and hook letters (Sami, Azerbaijani, West African languages)
  'ƀb', 'ɃB', 'ƁB', 'ƇC', 'ƈc', 'ȻC', 'ȼc', 'ƉD', 'ƊD', 'ȡd', 'ƎE', 'ǝe', 'ƐE', 'ɆE', 'ɇe',
  'ƏA', 'ƑF', 'ƓG', 'ǤG', 'ǥg', 'ƗI', 'ɈJ', 'ɉj', 'ȷj', 'ƘK', 'ƙk', 'ȽL', 'ƚl', 'ȴl', 'ƝN',
  'ƞn', 'ȠN', 'ȵn', 'ƆO', 'ƟO', 'ƤP', 'ƥp', 'ɌR', 'ɍr', 'ƬT', 'ƭt', 'ƫt', 'ƮT', 'ȾT', 'ȶt',
  'ɄU', 'ƲV', 'ƳY', 'ƴy', 'ɎY', 'ɏy', 'ƵZ', 'ƶz', 'ȤZ', 'ȥz', 'ƷZ', 'ǮZ', 'ǯz', 'ȺA',
  ['Ƣ', 'OI'], ['ƣ', 'oi'], ['Ȣ', 'OU'], ['ȣ', 'ou'], ['ƕ', 'hv'], ['Ƕ', 'Hv'], ['ẞ', 'SS'],
  // IPA-block letters that occur in the same orthographies
  'əa', 'ɛe', 'ɔo', 'ɓb', 'ɗd', 'ɖd', 'ɠg', 'ɨi', 'ɲn', 'ɵo', 'ʉu', 'ʋv', 'ʒz',
] as (string | [string, string])[]).map((pair) => (typeof pair === 'string' ? [pair[0], pair.slice(1)] : pair)))

// The character jsPDF prints for anything without a WinAnsi spelling.
export const PDF_REPLACEMENT = '?'

function isWinAnsi(ch: string): boolean {
  const c = ch.codePointAt(0)!
  return ch === '\n' || ch === '\r' || (c >= 0x20 && c <= 0x7e) || (c >= 0xa0 && c <= 0xff) || CP1252_HIGH.has(ch)
}

const allWinAnsi = (s: string) => s !== '' && Array.from(s).every(isWinAnsi)

function replace(ch: string): string {
  if (isWinAnsi(ch)) return ch
  if (ch in REPLACE) return REPLACE[ch]
  if (ch in LETTERS) return LETTERS[ch]
  // Compatibility forms (ﬁ → fi, fullwidth Ａ → A), then letters with accents WinAnsi lacks
  // (ő → o, č → c). Anything still outside WinAnsi gets the visible replacement.
  const compat = ch.normalize('NFKC')
  if (allWinAnsi(compat)) return compat
  const bare = compat.normalize('NFD').replace(/\p{M}/gu, '').normalize('NFC')
  return allWinAnsi(bare) ? bare : PDF_REPLACEMENT
}

// Typographic spaces used as digit separators ("12 345", "1 234 567"): between two digits
// they become NBSP so the number stays on one line; elsewhere they are word spaces. A plain
// U+0020 is the writer's own word space and stays breakable.
const GROUP_SPACE = /(?<=\d)[\u2006\u2008\u2009\u200a\u205f](?=\d)/g

// A string jsPDF's standard fonts print as written. NFC first, so a decomposed å (a + ring,
// as macOS file names and some sources write it) stays one WinAnsi å.
export function toWinAnsi(text: string): string {
  let out = ''
  for (const ch of text.normalize('NFC').replace(GROUP_SPACE, NBSP)) out += replace(ch)
  return out
}

export function pdfText(pdf: jsPDF, text: string | string[], x: number, y: number, options?: TextOptionsLight): void {
  pdf.text(Array.isArray(text) ? text.map(toWinAnsi) : toWinAnsi(text), x, y, options)
}

// splitTextToSize measures the same WinAnsi string pdfText prints, so wrapping stays exact.
export function pdfLines(pdf: jsPDF, text: string, width: number): string[] {
  return pdf.splitTextToSize(toWinAnsi(text), width) as string[]
}
